import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy } from 'firebase/firestore';
import DetailPanel from './DetailPanel';
import { useTheme } from '../ThemeContext';

function timeAgo(timestamp) {
  const now = new Date();
  const then = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function StatusBadge({ status }) {
  const isUnreviewed = status === 'unreviewed' || status === 'pending';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: '0.3px',
      background: isUnreviewed ? '#FFF2E6' : '#E8F8EF',
      color: isUnreviewed ? '#E67E22' : '#27AE60',
      textTransform: 'uppercase',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: isUnreviewed ? '#E67E22' : '#27AE60',
      }} />
      {isUnreviewed ? 'Unreviewed' : 'Reviewed'}
    </span>
  );
}

function SeverityBadge({ category }) {
  const colors = {
    violence: { bg: '#FFE8E8', color: '#D63031', label: 'Violence' },
    selfHarm: { bg: '#FFF0E6', color: '#E67E22', label: 'Self-Harm' },
    weapons: { bg: '#FFE8E8', color: '#D63031', label: 'Weapons' },
    sexual: { bg: '#F3E8FF', color: '#8B5CF6', label: 'Sexual Assault' },
  };
  const style = colors[category] || { bg: '#FFE8E8', color: '#D63031', label: 'Critical' };
  
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: style.bg, color: style.color, textTransform: 'uppercase', letterSpacing: '0.3px',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: style.color }} />
      {style.label}
    </span>
  );
}

function Dashboard({ admin, onLogout }) {
  const { t } = useTheme();
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [flags, setFlags] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [reportsPage, setReportsPage] = useState(50);
  const [flagsPage, setFlagsPage] = useState(50);

  // Listen to Firestore reports collection - scoped to this school
  useEffect(() => {
    const schoolDomain = admin.schoolId || admin.email?.split('@')[1] || '';
    
    const q = query(
      collection(db, 'reports'),
      where('schoolDomain', '==', schoolDomain),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => {
        const docData = d.data();
        return {
          id: d.id,
          ...docData,
          // Map fields from your app's report format to admin portal format
          reportedBy: {
            name: docData.reporterUserName || 'Unknown',
            email: docData.reporterUserEmail || '',
            major: docData.reporterMajor || '',
          },
          reportedUser: {
            name: docData.reportedUserName || 'Unknown',
            email: docData.reportedUserEmail || '',
            major: docData.reportedUserMajor || '',
          },
          reason: docData.reasonText || docData.reason || 'No reason provided',
          timestamp: docData.createdAt,
          status: docData.status === 'pending' ? 'unreviewed' : (docData.status || 'unreviewed'),
          chatContext: docData.chatContext || [],
        };
      });
      setReports(data);
      setLoading(false);
    }, (error) => {
      console.error('Error loading reports:', error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [admin]);

  // Listen to Firestore flaggedChats collection - scoped to this school
  useEffect(() => {
    const schoolDomain = admin.schoolId || admin.email?.split('@')[1] || '';
    
    const q = query(
      collection(db, 'flaggedChats'),
      where('schoolDomain', '==', schoolDomain),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => {
        const docData = d.data();
        return {
          id: d.id,
          ...docData,
          user: {
            name: docData.senderName || 'Unknown',
            email: docData.senderEmail || '',
            major: docData.senderMajor || '',
          },
          keyword: docData.keyword || '',
          matchedText: docData.matchedText || docData.originalMessage || '',
          timestamp: docData.createdAt,
          status: docData.status || 'unreviewed',
          category: docData.category || 'violence',
          chatContext: docData.chatContext || [],
        };
      });
      setFlags(data);
    }, (error) => {
      console.error('Error loading flagged chats:', error);
    });
    
    return () => unsubscribe();
  }, [admin]);

  const unreviewedReports = reports.filter(r => r.status === 'unreviewed' || r.status === 'pending').length;
  const unreviewedFlags = flags.filter(f => f.status === 'unreviewed').length;

  const filteredReportsAll = filterStatus === 'all' 
    ? reports.filter(r => r.status !== 'deleted') 
    : reports.filter(r => {
      if (filterStatus === 'unreviewed') return r.status === 'unreviewed' || r.status === 'pending';
      return r.status === filterStatus;
    });
  const filteredFlagsAll = filterStatus === 'all' 
    ? flags.filter(f => f.status !== 'deleted') 
    : flags.filter(f => f.status === filterStatus);
  
  const filteredReports = filteredReportsAll.slice(0, reportsPage);
  const filteredFlags = filteredFlagsAll.slice(0, flagsPage);
  const deletedReports = reports.filter(r => r.status === 'deleted');
  const deletedFlags = flags.filter(f => f.status === 'deleted');
  const allDeleted = [...deletedReports.map(r => ({ ...r, itemType: 'report' })), ...deletedFlags.map(f => ({ ...f, itemType: 'flag' }))];
  const hasMoreReports = filteredReportsAll.length > reportsPage;
  const hasMoreFlags = filteredFlagsAll.length > flagsPage;

  const handleDelete = async (id, type) => {
    const collectionName = type === 'report' ? 'reports' : 'flaggedChats';
    try {
      await updateDoc(doc(db, collectionName, id), { 
        status: 'deleted',
        deletedAt: new Date(),
        deletedBy: admin.email,
        autoDeleteAfter: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      });
      setSelectedItem(null);
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleMarkReviewed = async (id, type) => {
    const collectionName = type === 'report' ? 'reports' : 'flaggedChats';
    try {
      await updateDoc(doc(db, collectionName, id), { 
        status: 'reviewed',
        reviewedAt: new Date(),
        reviewedBy: admin.email,
      });
      if (selectedItem?.id === id) {
        setSelectedItem(prev => ({ ...prev, status: 'reviewed' }));
      }
    } catch (err) {
      console.error('Error marking as reviewed:', err);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: t.bgGradient,
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      {/* Top Nav */}
      <div style={{
        background: t.nav,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${t.navBorder}`,
        padding: '0 32px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img 
            src="/logo.png" 
            alt="PeerStudy" 
            style={{
              width: 36, height: 36, borderRadius: 10,
              boxShadow: '0 2px 8px rgba(59,125,216,0.25)',
            }}
          />
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: t.text }}>PeerStudy</span>
            <span style={{ fontSize: 14, color: '#8E8E93', fontWeight: 500, marginLeft: 6 }}>Admin</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{admin.school || 'School'}</div>
            <div style={{ fontSize: 11, color: '#8E8E93' }}>{admin.email}</div>
          </div>
          <button
  onClick={() => {
    if (window.confirm('Are you sure you want to sign out?')) {
      onLogout();
    }
  }}
            style={{
              padding: '8px 16px', borderRadius: 10,
              border: `1.5px solid ${t.inputBorder}`, background: t.card,
              fontSize: 13, fontWeight: 600, color: '#8E8E93',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 24px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 28 }}>
          {[
            { label: 'Total Reports', value: reports.filter(r => r.status !== 'deleted').length, icon: '📋', color: '#3B7DD8' },
            { label: 'Flagged Chats', value: flags.filter(f => f.status !== 'deleted').length, icon: '🚨', color: '#D63031' },
            { label: 'Needs Review', value: unreviewedReports + unreviewedFlags, icon: '⏳', color: '#E67E22' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: t.card,
              backdropFilter: 'blur(12px)', borderRadius: 18, padding: '20px 24px',
              border: `1px solid ${t.cardBorder}`,
              boxShadow: t.cardShadow,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {stat.icon} {stat.label}
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: stat.color, marginTop: 6, letterSpacing: '-1px' }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20,
        }}>
          <div style={{
            display: 'flex', background: t.card, borderRadius: 14,
            padding: 4, gap: 4, border: `1px solid ${t.cardBorder}`,
          }}>
            {[
              { key: 'reports', label: 'Reports', count: unreviewedReports },
              { key: 'flags', label: 'Flagged Chats', count: unreviewedFlags },
              { key: 'deleted', label: 'Deleted', count: 0 },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setFilterStatus('all'); setReportsPage(50); setFlagsPage(50); }}
                style={{
                  padding: '10px 20px', borderRadius: 11, border: 'none',
                  background: activeTab === tab.key
                    ? 'linear-gradient(135deg, #3B7DD8 0%, #2E6BC4 100%)' : 'transparent',
                  color: activeTab === tab.key ? '#fff' : '#8E8E93',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: activeTab === tab.key ? '0 2px 8px rgba(59,125,216,0.25)' : 'none',
                }}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span style={{
                    background: activeTab === tab.key ? 'rgba(255,255,255,0.25)' : '#FFE8E8',
                    color: activeTab === tab.key ? '#fff' : '#D63031',
                    fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {activeTab !== 'deleted' && ['all', 'unreviewed', 'reviewed'].map(f => (
              <button
                key={f}
                onClick={() => { setFilterStatus(f); setReportsPage(50); setFlagsPage(50); }}
                style={{
                  padding: '7px 14px', borderRadius: 9, border: 'none',
                  background: filterStatus === f ? t.filterActiveBg : t.filterInactiveBg,
                  color: filterStatus === f ? t.filterActive : t.filterInactive,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  textTransform: 'capitalize',
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div style={{
            textAlign: 'center', padding: 60, color: '#8E8E93',
            background: t.emptyBg, borderRadius: 18, fontSize: 15,
          }}>
            Loading...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeTab === 'reports' ? (
              <>
                {filteredReports.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: 60, color: '#C7C7CC',
                    background: t.emptyBg, borderRadius: 18, fontSize: 15,
                  }}>
                    No reports yet.
                  </div>
                ) : (
                  filteredReports.map(report => (
                    <div
                      key={report.id}
                      onClick={() => { setSelectedItem(report); setSelectedType('report'); }}
                      style={{
                        background: t.card,
                        backdropFilter: 'blur(12px)', borderRadius: 18, padding: '18px 22px',
                        border: report.status === 'unreviewed' || report.status === 'pending'
                          ? '1.5px solid rgba(230,126,34,0.15)' : `1px solid ${t.cardBorder}`,
                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 38, height: 38, borderRadius: 12,
                            background: 'linear-gradient(135deg, #F2F2F7 0%, #E5E5EA 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, fontWeight: 700, color: '#8E8E93',
                          }}>
                            {report.reportedUser?.name?.[0] || '?'}
                          </div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>
                              {report.reportedUser?.name || 'Unknown'}
                              <span style={{ fontSize: 13, color: '#8E8E93', fontWeight: 400, marginLeft: 6 }}>
                                reported by {report.reportedBy?.name || 'Unknown'}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, color: '#8E8E93', marginTop: 2 }}>
                              {report.reason}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <StatusBadge status={report.status} />
                          <span style={{ fontSize: 12, color: '#C7C7CC', fontWeight: 500 }}>
                            {timeAgo(report.timestamp)}
                          </span>
                        </div>
                      </div>
                      {report.details && (
                        <div style={{
                          background: t.sectionBg, borderRadius: 12, padding: '10px 14px',
                          fontSize: 13, color: '#636366', lineHeight: 1.45,
                        }}>
                          {report.details}
                        </div>
                      )}
                    </div>
                  ))
                )}
                {hasMoreReports && (
                  <button
                    onClick={() => setReportsPage(prev => prev + 50)}
                    style={{
                      width: '100%', padding: 14, borderRadius: 14, border: 'none',
                      background: t.card, color: '#3B7DD8',
                      fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      marginTop: 4,
                    }}
                  >
                    Load More ({filteredReportsAll.length - reportsPage} remaining)
                  </button>
                )}
              </>
            ) : activeTab === 'flags' ? (
              <>
                {filteredFlags.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: 60, color: '#C7C7CC',
                    background: t.emptyBg, borderRadius: 18, fontSize: 15,
                  }}>
                    No flagged chats yet.
                  </div>
                ) : (
                  filteredFlags.map(flag => (
                    <div
                      key={flag.id}
                      onClick={() => { setSelectedItem(flag); setSelectedType('flag'); }}
                      style={{
                        background: t.card,
                        backdropFilter: 'blur(12px)', borderRadius: 18, padding: '18px 22px',
                        border: flag.status === 'unreviewed'
                          ? '1.5px solid rgba(214,48,49,0.12)' : `1px solid ${t.cardBorder}`,
                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 38, height: 38, borderRadius: 12,
                            background: 'linear-gradient(135deg, #FFE8E8 0%, #FFD4D4 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, fontWeight: 700, color: '#D63031',
                          }}>
                            !
                          </div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>
                              {flag.user?.name || 'Unknown'}
                              <span style={{
                                fontSize: 12, fontWeight: 700, color: '#D63031',
                                background: '#FFE8E8', padding: '2px 8px', borderRadius: 6,
                                marginLeft: 8, fontFamily: "'SF Mono', 'Fira Code', monospace",
                              }}>
                                {flag.keyword}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, color: '#8E8E93', marginTop: 2 }}>
                              {flag.user?.email} · {flag.user?.major}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <SeverityBadge category={flag.category} />
                          <StatusBadge status={flag.status} />
                          <span style={{ fontSize: 12, color: '#C7C7CC', fontWeight: 500 }}>
                            {timeAgo(flag.timestamp)}
                          </span>
                        </div>
                      </div>
                      <div style={{
                        background: t.sectionBg, borderRadius: 12, padding: '10px 14px',
                        fontSize: 13, color: t.textSecondary, lineHeight: 1.45,
                        border: `1px solid ${t.cardBorder}`,
                      }}>
                        "{flag.matchedText}"
                      </div>
                    </div>
                  ))
                )}
                {hasMoreFlags && (
                  <button
                    onClick={() => setFlagsPage(prev => prev + 50)}
                    style={{
                      width: '100%', padding: 14, borderRadius: 14, border: 'none',
                      background: t.card, color: '#3B7DD8',
                      fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      marginTop: 4,
                    }}
                  >
                    Load More ({filteredFlagsAll.length - flagsPage} remaining)
                  </button>
                )}
              </>
            ) : (
              <>
                {allDeleted.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: 60, color: '#C7C7CC',
                    background: t.emptyBg, borderRadius: 18, fontSize: 15,
                  }}>
                    No deleted items.
                  </div>
                ) : (
                  allDeleted.map(item => (
                    <div
                      key={item.id}
                      onClick={() => { setSelectedItem(item); setSelectedType(item.itemType); }}
                      style={{
                        background: t.card,
                        borderRadius: 18, padding: '18px 22px',
                        border: `1px solid ${t.cardBorder}`,
                        cursor: 'pointer', boxShadow: t.cardShadow,
                        opacity: 0.7,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 38, height: 38, borderRadius: 12,
                            background: '#F2F2F7',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, fontWeight: 700, color: '#C7C7CC',
                          }}>
                            {item.itemType === 'report' ? (item.reportedUser?.name?.[0] || '?') : '!'}
                          </div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#8E8E93' }}>
                              {item.itemType === 'report' 
                                ? `${item.reportedUser?.name || 'Unknown'} — ${item.reason}`
                                : `${item.user?.name || 'Unknown'} — "${(item.matchedText || '').substring(0, 50)}"`
                              }
                            </div>
                            <div style={{ fontSize: 12, color: '#C7C7CC', marginTop: 2 }}>
                              {item.itemType === 'report' ? 'Report' : 'Flagged Chat'} · Deleted {item.deletedBy ? `by ${item.deletedBy}` : ''}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Restore this item?')) {
                              updateDoc(doc(db, item.itemType === 'report' ? 'reports' : 'flaggedChats', item.id), { 
                                status: 'reviewed',
                                deletedAt: null,
                                deletedBy: null,
                                autoDeleteAfter: null,
                              });
                            }
                          }}
                          style={{
                            padding: '8px 14px', borderRadius: 10, border: 'none',
                            background: '#3B7DD8', color: '#fff',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selectedItem && (
        <DetailPanel
          item={selectedItem}
          type={selectedType}
          onClose={() => setSelectedItem(null)}
          onMarkReviewed={handleMarkReviewed}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

export default Dashboard;